import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { format } from "date-fns";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import useLabels from "../../hooks/useLabels";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import SecondaryBar from "../SecondaryBar";
import { CTA, ConfirmationModal, Modal } from "../Generic";
import { MultipleSelect, Select } from "../FormComponents";
import { SkeletonTable } from "../Skeleton/Skeleton";
import { Icon } from "../Icon";
import { ROLES } from "../../constants/base";
import {
  selectActiveWinery,
  selectApplications,
  selectConfigs,
  selectLots,
  selectTanks,
  selectUsers,
  selectWineMaking,
} from "../../context/selectors";
import {
  setApplications,
  addApplication,
  removeApplication,
} from "../../context/protocols/applicationSlice";
import { removeOp } from "../../context/operations/operationsSlice";
import { removeExpOp } from "../../context/expOperations/expOperationsSlice";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import { setUsers } from "../../context/users/usersSlice";
import { setTanks } from "../../context/tanks/tanksSlice";
import { setLots } from "../../context/lots/lotsSlice";
import {
  getProtocolApplications,
  createProtocolApplication,
  deleteProtocolApplication,
  getProtocols,
  getTanks,
  getUsers,
  getLots,
} from "../../services/utils";
import { toastAxios } from "../../services/toastAxios";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../Table/TableCell";

import "./styles/index.scss";

const Table = lazy(() => import("../Table"));

// Operations that work on batches (not tanks)
const BATCH_OPERATIONS = ["DESTEMMING", "ANALYSIS"];

const ProtocolApplications = ({ isMobile, dateFrom: dateFromProp, dateTo: dateToProp }) => {
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;

  const activeWinery = useSelector(selectActiveWinery);
  const applications = useSelector(selectApplications);
  const wineMaking = useSelector(selectWineMaking);
  const tanks = useSelector(selectTanks);
  const lots = useSelector(selectLots);
  const users = useSelector(selectUsers);
  const configs = useSelector(selectConfigs);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [protocolFilter, setProtocolFilter] = useState("");
  const [dateFromInternal, setDateFromInternal] = useState(null);
  const [dateToInternal, setDateToInternal] = useState(null);
  const [targetType, setTargetType] = useState("tanks");
  const [isApplicationsLoading, setIsApplicationsLoading] = useState(true);

  // Use props if provided (from OperationsHub), otherwise use internal state (standalone mode)
  const dateFrom = dateFromProp !== undefined ? dateFromProp : dateFromInternal;
  const dateTo = dateToProp !== undefined ? dateToProp : dateToInternal;
  const setDateFrom = dateFromProp !== undefined ? () => {} : setDateFromInternal;
  const setDateTo = dateToProp !== undefined ? () => {} : setDateToInternal;

  const modalRef = useRef(null);

  const customIdCreate = "custom-id-create-protocol-application";
  const customIdDelete = "custom-id-delete-protocol-application";

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      protocol: null,
      tanks: [],
      batch: null,
      start_date: new Date(),
      priority: null,
      cellarmen: [],
    },
  });

  const selectedProtocol = watch("protocol");

  // Auto-switch target based on protocol operations
  useEffect(() => {
    if (selectedProtocol?.value) {
      const proto = wineMaking.find((p) => p.id === selectedProtocol.value);
      const ops = proto?.operations || [];
      const hasBatchOp = ops.some((op) =>
        BATCH_OPERATIONS.includes(op?.operation?.toUpperCase?.())
      );
      setTargetType(hasBatchOp ? "batch" : "tanks");
    }
  }, [selectedProtocol, wineMaking]);

  useEffect(() => {
    const init = async () => {
      const promises = [];
      if (!applications || applications.length === 0) promises.push(loadApplications());
      if (!wineMaking || wineMaking.length === 0) promises.push(loadProtocols());
      if (!tanks || tanks.length === 0) promises.push(loadTanks());
      if (!lots || lots.length === 0) promises.push(loadLots());
      if (!users || users.length === 0) promises.push(loadUsers());
      try {
        await Promise.all(promises);
      } finally {
        setIsApplicationsLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    if (isModalOpen) { el.close(); el.showModal(); } else { el.close(); }
  }, [isModalOpen]);

  const loadApplications = async () => {
    const data = await getProtocolApplications(axiosPrivate);
    dispatch(setApplications(data || []));
  };
  
  const loadProtocols = async () => {
    const data = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(data || []));
  };
  const loadTanks = async () => {
    const data = await getTanks(activeWinery.id, axiosPrivate);
    dispatch(setTanks(data || []));
  };
  const loadLots = async () => {
    const data = await getLots(activeWinery.id, axiosPrivate);
    dispatch(setLots(data || []));
  };
  const loadUsers = async () => {
    const data = await getUsers(axiosPrivate);
    dispatch(setUsers(data || []));
  };

  const openModal = () => {
    reset({ protocol: null, tanks: [], batch: null, start_date: new Date(), priority: null, cellarmen: [] });
    setTargetType("tanks");
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const submitNewApplication = async (data) => {
    const payload = {
      winemaking_protocol_id: data.protocol?.value,
      batch_id: targetType === "batch" ? (data.batch?.value ?? null) : null,
      tanks: targetType === "tanks" ? (data.tanks || []).map((t) => t.value) : null,
      start_date: data.start_date
        ? format(new Date(data.start_date), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd"),
      priority: data.priority?.value ?? 2,
      cellarman_ids: (data.cellarmen || []).map((u) => u.value),
    };
    const response = await toastAxios(
      () => createProtocolApplication(payload, axiosPrivate),
      customIdCreate,
      { name: data.protocol?.label || "" },
      getLabel
    );
    if (response?.success) {
      const appId = response?.application_id || response?.id;

      // Normalize response to match table structure
      const normalizedApp = {
        id: response.application_id,
        protocol_name: response.protocol_name,
        winemaking_protocol_id: response.winemaking_protocol_id || payload.winemaking_protocol_id,
        batch_id: response.batch_id || payload.batch_id,
        batch_name: response.batch_name,
        tanks: response.tanks || payload.tanks,
        start_date: response.start_date || payload.start_date,
        applied_at: response.applied_at || new Date().toISOString().split('T')[0],
        status: response.status || 'ACTIVE',
        total_steps: response.total_steps,
        executed_steps: response.configured_steps || 0,
        invalid_steps: response.invalid_steps || 0,
        ...response, // Keep all other fields from response
      };

      dispatch(addApplication(normalizedApp));
      closeModal();
      if (appId) navigate(`/protocol-applications/${appId}`);
      else loadApplications();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const response = await toastAxios(
      () => deleteProtocolApplication(deleteTarget.id, "Eliminato dall'utente", axiosPrivate),
      customIdDelete,
      { name: deleteTarget.protocol_name || String(deleteTarget.id) },
      getLabel
    );
    if (response?.success) {
      dispatch(removeApplication({ id: deleteTarget.id }));
      response.cancelled_operation_ids?.forEach(opId => {
        dispatch(removeOp({ id: opId }));
        dispatch(removeExpOp({ id: opId }));
      });
    }
    setIsDeleteOpen(false);
    setDeleteTarget(null);
  };

  const statusLabel = (s) => getLabel(`protocolApplicationStatus_${s}`) || s;

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("id", {
      header: getLabel("protocolApplicationId"),
      cell: TableCell,
      meta: { type: "number" },
    }),
    columnHelper.accessor(
      (row) => {
        const proto = wineMaking.find((p) => p.id === row.winemaking_protocol_id);
        return proto?.name || row.protocol_name;
      },
      {
        header: getLabel("protocolApplicationColProtocol"),
        cell: TableCell,
        meta: { type: "text", filterVariant: "text" },
      }
    ),
    columnHelper.accessor(
      (row) => {
        if (row.batch_id) {
          return row?.batch_name || lots.find((l) => l.id === row.batch_id)?.name || `${getLabel("protocolApplicationTargetBatch")} #${row.batch_id}`;
        }
        if (row.tanks?.length > 0)
          return row.tanks.map((tid) => tanks.find((t) => t.id === tid)?.name || `T${tid}`).join(", ");
        return "—";
      },
      {
        header: getLabel("protocolApplicationColTarget"),
        cell: TableCell,
        meta: { type: "text", isFilterDisabled: true },
      }
    ),
    columnHelper.accessor(
      (row) => row.start_date ? format(new Date(row.start_date), configs.shortDateFormat || "dd/MM/yyyy") : "—",
      {
        header: getLabel("protocolApplicationStartDate"),
        cell: TableCell,
        meta: { type: "text", isFilterDisabled: true },
      }
    ),
    columnHelper.accessor(
      (row) => row.applied_at ? format(new Date(row.applied_at), configs.shortDateFormat || "dd/MM/yyyy") : "—",
      {
        header: getLabel("protocolApplicationApplicationDate"),
        cell: TableCell,
        meta: { type: "text", isFilterDisabled: true },
      }
    ),
    columnHelper.accessor("status", {
      header: getLabel("protocolApplicationColStatus"),
      cell: (info) => (
        <span className={`protocol_applications_status_badge ${info.getValue()}`}>
          {statusLabel(info.getValue())}
        </span>
      ),
      meta: { type: "text", filterVariant: "select" },
    }),
    columnHelper.accessor(
      (row) => `${row.executed_steps || 0}/${row.total_steps || 0}`,
      {
        header: getLabel("protocolApplicationColProgress"),
        cell: (info) => <span className="protocol_applications_progress">{info.getValue()}</span>,
        meta: { type: "text", isFilterDisabled: true },
      }
    ),
    columnHelper.accessor(
      (row) => row.invalid_steps || 0,
      {
        header: getLabel("protocolApplicationColInvalidSteps"),
        cell: (info) =>
          info.getValue() > 0 ? (
            <span className="protocol_applications_invalid_badge">{info.getValue()} ❌</span>
          ) : (
            <span style={{ color: "gray", fontSize: 12 }}>—</span>
          ),
        meta: { type: "number", isFilterDisabled: true },
      }
    ),
  ];

  const filteredApplications = applications.filter((app) => {
    if (statusFilter && app.status !== statusFilter) return false;
    if (protocolFilter) {
      const proto = wineMaking.find((p) => p.id === app.winemaking_protocol_id);
      const name = proto?.name || app.protocol_name || "";
      if (!name.toLowerCase().includes(protocolFilter.toLowerCase())) return false;
    }
    if (dateFrom && new Date(app.start_date) < dateFrom) return false;
    if (dateTo && new Date(app.start_date) > dateTo) return false;
    return true;
  });

  const protocolOptions = wineMaking.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.protocol_type || p.subtype || ""} · ${p.operations?.length || 0} step)`,
  }));
  const tankOptions = tanks.map((t) => ({
    value: t.id,
    label: `${t.name}${t.quantity != null ? ` · ${t.quantity}L` : ""}`,
  }));
  const lotOptions = lots.map((l) => ({
    value: l.id,
    label: `${l.name} (${l.state || l.status || ""})`,
  }));
  const cellarmenOptions = users
    .filter((u) => u.role === ROLES.USER || u.roles?.includes?.(ROLES.USER))
    .map((u) => ({ value: u.id, label: u.username || u.name }));
  const priorityOptions = [
    { value: 2, label: getLabel("operationPriority2") },
    { value: 1, label: getLabel("operationPriority1") },
    { value: 0, label: getLabel("operationPriority0") },
  ];
  const statusOptions = ["ACTIVE", "COMPLETED", "INVALIDATED", "CANCELLED"];

  return (
    <div className={dateFromProp === undefined ? "primary_container" : ""}>
      {(false && dateFromProp === undefined) && (
        <>
          {/* <SecondaryBar breadCrumb={[getLabel("protocolApplicationsNavLink")]} isBasePathNeeded={false} /> */}

          <div className="primary_container_menu">
            {/* <h2>{getLabel("protocolApplications")}</h2> */}
            {(role === ROLES.CLIENT || role === ROLES.USER) && (
              <CTA customClassName="create" onClick={() => navigate("/protocol-applications/new")}>
                {getLabel("protocolApplicationNew")} <Icon icon="fa-plus" />
              </CTA>
            )}
          </div>
        </>
      )}

      {/* Filters */}
      <div className="protocol_applications_filters">
        <div className="filter_field">
          <label>{getLabel("protocolApplicationFilterStatus")}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{getLabel("protocolApplicationFilterAll")}</option>
            {statusOptions.map((val) => (
              <option key={val} value={val}>{statusLabel(val)}</option>
            ))}
          </select>
        </div>
        {dateFromProp === undefined && (
          <>
            <div className="filter_field">
              <label>{getLabel("dateFrom")}</label>
              <ReactDatePicker
                selected={dateFrom}
                onChange={setDateFrom}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                dateFormat="dd/MM/yyyy"
                placeholderText="gg/mm/aaaa"
                isClearable
              />
            </div>
            <div className="filter_field">
              <label>{getLabel("dateTo")}</label>
              <ReactDatePicker
                selected={dateTo}
                onChange={setDateTo}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom}
                dateFormat="dd/MM/yyyy"
                placeholderText="gg/mm/aaaa"
                isClearable
              />
            </div>
          </>
        )}
        <div className="filter_field">
          <label>{getLabel("protocolApplicationFilterProtocol")}</label>
          <input
            type="text"
            value={protocolFilter}
            onChange={(e) => setProtocolFilter(e.target.value)}
            placeholder={getLabel("protocolApplicationFilterSearchProtocol")}
            style={{ height: 40, border: "2px solid rgba(33,37,31,0.32)", borderRadius: 8, padding: "0 12px", fontFamily: "inherit", fontSize: 14 }}
          />
        </div>
        {(role === ROLES.CLIENT) && (
          <CTA customClassName="create" onClick={() => navigate("/protocol-applications/new")}>
            {getLabel("protocolApplicationNew")} <Icon icon="fa-plus" />
          </CTA>
        )}
      </div>

      
      {!isApplicationsLoading && filteredApplications?.length === 0 && (
        <h5 className="alert_no_info">{getLabel("noProtocolApplicationsFound")}</h5>
      )}

      {/* Table */}
      {isApplicationsLoading && <SkeletonTable colCount={8} />}
      {!isApplicationsLoading && filteredApplications?.length > 0 && <Suspense fallback={<SkeletonTable colCount={8} />}>
        <Table
          defaultData={filteredApplications}
          columns={columns}
          rowClickHandler={(row) => navigate(`/protocol-applications/${row.original.id}`)}
          isEditable={false}
          isPagination={true}
          type="application"
          rowActions={(row) => (
            <div style={{ display: "flex", gap: 8 }}>
              <CTA customClassName="small" onClick={(e) => { e.stopPropagation(); navigate(`/protocol-applications/${row.original.id}`); }}>
                <Icon icon="fa-eye" />
              </CTA>
              {(role === ROLES.CLIENT || role === ROLES.USER) && (
                <CTA customClassName="small" isRed onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); setIsDeleteOpen(true); }}>
                  <Icon icon="fa-trash" />
                </CTA>
              )}
            </div>
          )}
        />
      </Suspense>}

      {/* New Application Modal */}
      {isModalOpen && (
        <Modal forwardRef={modalRef} closeHandle={closeModal} isOpen={isModalOpen} customClassName="winemaking">
          <form onSubmit={handleSubmit(submitNewApplication)} autoComplete="off" noValidate>
            <div className="protocol_application_modal">
              <h2 className="modal_title">{getLabel("protocolApplicationNew")}</h2>

              <div className="modal_field_group">
                <Select
                  control={control}
                  name="protocol"
                  label={getLabel("protocolApplicationSelectProtocol")}
                  options={protocolOptions}
                  required={getLabel("inputRequiredError")}
                  isSearchable
                  error={errors}
                />
              </div>

              <div className="modal_field_group">
                <label>{getLabel("protocolApplicationApplyOn")}</label>
                <div className="modal_radio_group">
                  <label>
                    <input type="radio" checked={targetType === "tanks"} onChange={() => setTargetType("tanks")} />
                    {getLabel("protocolApplicationTargetTanks")}
                  </label>
                  <label>
                    <input type="radio" checked={targetType === "batch"} onChange={() => setTargetType("batch")} />
                    {getLabel("protocolApplicationTargetBatch")}
                  </label>
                </div>
              </div>

              {targetType === "tanks" ? (
                <div className="modal_field_group">
                  <MultipleSelect
                    control={control}
                    name="tanks"
                    label={getLabel("protocolApplicationTargetTanks")}
                    options={tankOptions}
                    required={getLabel("inputRequiredError")}
                    placeholder={getLabel("protocolApplicationSelTanksPlaceholder")}
                    error={errors}
                  />
                </div>
              ) : (
                <div className="modal_field_group">
                  <Select
                    control={control}
                    name="batch"
                    label={getLabel("protocolApplicationTargetBatch")}
                    options={lotOptions}
                    required={getLabel("inputRequiredError")}
                    placeholder={getLabel("protocolApplicationSelBatchPlaceholder")}
                    isSearchable
                    error={errors}
                  />
                </div>
              )}

              <div className="modal_field_group">
                <label className="required">{getLabel("protocolApplicationStartDate")}</label>
                <Controller
                  control={control}
                  name="start_date"
                  render={({ field }) => (
                    <ReactDatePicker selected={field.value} onChange={field.onChange} dateFormat="dd/MM/yyyy" placeholderText="gg/mm/aaaa" />
                  )}
                />
              </div>

              <div className="modal_field_group">
                <Select
                  control={control}
                  name="priority"
                  label={getLabel("protocolApplicationPriorityLabel")}
                  options={priorityOptions}
                  error={errors}
                />
              </div>

              <div className="modal_field_group">
                <MultipleSelect
                  control={control}
                  name="cellarmen"
                  label={getLabel("protocolApplicationCellarmenLabel")}
                  options={cellarmenOptions}
                  placeholder={getLabel("protocolApplicationSelCellarmenPlaceholder")}
                  error={errors}
                />
              </div>

              <div className="modal_button_row">
                <CTA customClassName="dialog deny" onClick={closeModal} type="button">
                  {getLabel("modalDenyButton")}
                </CTA>
                <CTA customClassName="dialog confirm" isSubmit>
                  {getLabel("protocolApplicationApplyCTA")}
                </CTA>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        onConfirm={confirmDelete}
        onClose={() => { setIsDeleteOpen(false); setDeleteTarget(null); }}
        title="modalNoTitle"
        description={getLabel("modalDeleteProtocolApplicationDescription", {
          name: deleteTarget?.protocol_name || `#${deleteTarget?.id}`,
        })}
      />
    </div>
  );
};

export default ProtocolApplications;
